import { z } from 'zod';
import { certifiedEmailSchema, certifiedEmailTarget, outboundEmailSchema, outboundEmailTarget, splitSecret, } from '@xiltrion/correo';
/**
 * Fuentes de la entidad (GUIA-AGENTES.md §3.12): conexiones con credenciales que la entidad registra una vez en la
 * suite y luego asigna a cada app: almacenamiento, certificado de firma, correo saliente y correo certificado. Una entidad puede tener varias del mismo tipo (un bucket para SGDEA y otro para
 * Planning) y cada app recibe solo las que le asignaron, por la API de servicio y con sus secretos.
 *
 * Lo configura un superadministrador; el administrador de la entidad lo ve sin secretos. Sin el campo del secreto se
 * conserva el guardado solo si el destino no cambió, para que nadie lo mande a otro servidor cambiando la dirección.
 */
export const SOURCE_KINDS = ['STORAGE', 'CERTIFICATE', 'EMAIL', 'CERTIFIED_EMAIL'];
export const SOURCE_KIND_LABEL = {
    STORAGE: 'Almacenamiento',
    CERTIFICATE: 'Certificado de firma',
    EMAIL: 'Correo saliente',
    CERTIFIED_EMAIL: 'Correo certificado',
};
/** Las que se administran en la pestaña Almacenamiento; las de correo, en la pestaña Correo. */
export const FILE_SOURCE_KINDS = ['STORAGE', 'CERTIFICATE'];
export const MAIL_SOURCE_KINDS = ['EMAIL', 'CERTIFIED_EMAIL'];
/**
 * Para qué usa la app la fuente. Una app tiene a lo sumo una fuente por uso; si no tiene, pasa lo que dice
 * `unassigned`: usa lo suyo (el almacenamiento del servidor, el certificado de la plataforma) o se queda sin eso.
 */
export const SOURCE_SLOTS = {
    STORAGE: [
        { code: 'main', label: 'Archivos', description: 'Donde la app guarda los archivos de la entidad.', unassigned: 'Almacenamiento del servidor' },
        { code: 'backup', label: 'Respaldo', description: 'Copia de los archivos en otro lugar (SGDEA).', unassigned: 'Sin respaldo' },
    ],
    CERTIFICATE: [
        { code: 'signing', label: 'Firma', description: 'Certificado con el que la app firma documentos o facturas.', unassigned: 'Certificado de la plataforma' },
    ],
    EMAIL: [
        { code: 'main', label: 'Envíos', description: 'Con este correo la app le escribe al ciudadano y avisa a los funcionarios.', unassigned: 'Sin correo: no envía' },
    ],
    CERTIFIED_EMAIL: [
        { code: 'main', label: 'Certificados', description: 'Envíos con acuse de entrega y acta (Datacrédito Postal).', unassigned: 'Sin correo certificado' },
    ],
};
export const ALL_SOURCE_SLOTS = [...new Set(Object.values(SOURCE_SLOTS).flat().map((s) => s.code))];
export function isSlotOf(kind, slot) {
    return SOURCE_SLOTS[kind].some((s) => s.code === slot);
}
/**
 * Qué puede hacer el administrador de la entidad (el resto, solo un superadministrador de Datalog): registrar
 * almacenamientos, asignar el **respaldo** y elegir, entre los correos que ya tiene la entidad, el **principal** de cada
 * app (y sus envíos, §3.10). El almacenamiento principal, los certificados y el registro del correo los configura
 * Datalog, y un almacenamiento que alguna app usa como principal tampoco lo cambia ni lo borra el administrador.
 */
export const ENTITY_ADMIN_SOURCE_KINDS = ['STORAGE'];
export function entityAdminCanAssign(kind, slot) {
    return (kind === 'STORAGE' && slot === 'backup') || ((kind === 'EMAIL' || kind === 'CERTIFIED_EMAIL') && slot === 'main');
}
const name = z.string().trim().min(1, 'Ponle un nombre').max(120);
const secret = (max) => z.string().max(max).optional().nullable();
// ───── Almacenamiento (S3 o compatible: MinIO, Wasabi, R2, DigitalOcean Spaces…)
export const STORAGE_PRESETS = {
    aws: { label: 'Amazon S3', endpoint: '', region: 'us-east-1', forcePathStyle: false, hint: 'Deja el servidor vacío. Usa una llave de IAM con permiso solo sobre este bucket.' },
    minio: { label: 'MinIO', endpoint: 'https://minio.ejemplo.com', region: 'us-east-1', forcePathStyle: true, hint: 'MinIO necesita rutas con el nombre del bucket (path style).' },
    r2: { label: 'Cloudflare R2', endpoint: 'https://<cuenta>.r2.cloudflarestorage.com', region: 'auto', forcePathStyle: false, hint: 'Región «auto». La llave es un token de API de R2 con acceso al bucket.' },
    wasabi: { label: 'Wasabi', endpoint: 'https://s3.us-east-1.wasabisys.com', region: 'us-east-1', forcePathStyle: false, hint: 'Cambia la región del servidor si el bucket no está en us-east-1.' },
    spaces: { label: 'DigitalOcean Spaces', endpoint: 'https://nyc3.digitaloceanspaces.com', region: 'us-east-1', forcePathStyle: false, hint: 'El servidor lleva la región del Space (nyc3, sfo3…).' },
    custom: { label: 'Otro compatible con S3', endpoint: '', region: 'us-east-1', forcePathStyle: true, hint: 'Los datos los da quien administra el servicio.' },
};
const STORAGE_PRESET_KEYS = Object.keys(STORAGE_PRESETS);
/**
 * Cifrado en reposo de los archivos (docs/cifrado-y-llaves.md de la suite, fase 2):
 * - `bucket`: la app no pide nada; vale el cifrado por defecto que tenga el bucket.
 * - `sse-s3`: cifrado del servicio con sus llaves (`AES256`).
 * - `sse-kms`: cifrado con la llave del KMS de la entidad (`aws:kms` + `kmsKeyId`): la entidad la controla y la puede
 *   revocar. Solo AWS S3 y MinIO con KES lo ofrecen.
 */
export const STORAGE_ENCRYPTION_MODES = ['bucket', 'sse-s3', 'sse-kms'];
export const STORAGE_ENCRYPTION = {
    bucket: { label: 'El del bucket', description: 'La app no pide cifrado: vale el que tenga el bucket por defecto.' },
    'sse-s3': { label: 'Del servicio (SSE-S3)', description: 'Cada archivo se cifra con las llaves del servicio de almacenamiento.' },
    'sse-kms': { label: 'Llave de la entidad (SSE-KMS)', description: 'Cada archivo se cifra con la llave del KMS de la entidad, que ella puede revocar.' },
};
/** Por qué la configuración de cifrado no sirve, o `null`. */
export function storageEncryptionProblem(s) {
    return s.encryption === 'sse-kms' && !s.kmsKeyId ? 'Con la llave de la entidad (SSE-KMS) hay que indicar la llave' : null;
}
/**
 * Lo que la app agrega a `PutObject`, `CopyObject` y `CreateMultipartUpload` según el almacenamiento. Una fuente
 * guardada antes de la fase 2 no trae `encryption`: es `bucket`.
 */
export function storagePutEncryption(s) {
    if (s.encryption === 'sse-s3')
        return { ServerSideEncryption: 'AES256' };
    if (s.encryption === 'sse-kms' && s.kmsKeyId)
        return { ServerSideEncryption: 'aws:kms', SSEKMSKeyId: s.kmsKeyId };
    return {};
}
export const storageSourceSchema = z.object({
    kind: z.literal('STORAGE'),
    name,
    preset: z.enum(STORAGE_PRESET_KEYS).default('custom'),
    /** Vacío: Amazon S3. */
    endpoint: z.string().trim().url('Servidor no válido').max(300).optional().nullable().or(z.literal('').transform(() => null)).transform((v) => v || null),
    region: z.string().trim().min(1).max(40).default('us-east-1'),
    bucket: z.string().trim().min(3, 'Indica el bucket').max(63).regex(/^[a-z0-9][a-z0-9.-]+[a-z0-9]$/, 'Bucket no válido: minúsculas, números, puntos y guiones'),
    /** Carpeta dentro del bucket, sin «/» al inicio ni al final. */
    prefix: z.string().trim().max(200).regex(/^[A-Za-z0-9._\-/]*$/, 'Carpeta no válida').optional().nullable().transform((v) => (v ? v.replace(/^\/+|\/+$/g, '') || null : null)),
    forcePathStyle: z.boolean().default(false),
    accessKeyId: z.string().trim().min(1, 'Indica la llave de acceso').max(200),
    secretAccessKey: secret(500),
    /** El bucket tiene bloqueo de objetos (WORM): la app no borra ni reemplaza lo que sube. Informativo. */
    worm: z.boolean().default(false),
    /** Cómo pide la app que se cifre cada archivo que sube (`storagePutEncryption`). */
    encryption: z.enum(STORAGE_ENCRYPTION_MODES).default('bucket'),
    /** Con `sse-kms`: la llave del KMS de la entidad (ARN, alias o id). La política del bucket debería exigirla. */
    kmsKeyId: z.string().trim().max(2048).regex(/^[A-Za-z0-9/_+=.@:-]+$/, 'Llave no válida: la ARN, el alias o el id de la llave').optional().nullable().or(z.literal('').transform(() => null)).transform((v) => v || null),
});
// ───── Certificado de firma (.p12 / .pfx)
export const CERTIFICATE_ACCEPT = 'application/x-pkcs12,.p12,.pfx';
export const CERTIFICATE_MAX_BYTES = 64 * 1024;
export const certificateSourceSchema = z.object({
    kind: z.literal('CERTIFICATE'),
    name,
    /** El .p12 en base64. Sin él se conserva el guardado. */
    p12Base64: z.string().max(Math.ceil((CERTIFICATE_MAX_BYTES * 4) / 3) + 8).optional().nullable(),
    /** Contraseña del .p12 (puede ser vacía). Sin ella se conserva la guardada, si el .p12 no cambió. */
    password: secret(200),
    /** Sello de tiempo (TSA) opcional para las firmas. */
    tsaUrl: z.string().trim().url('Dirección no válida').max(300).optional().nullable().or(z.literal('').transform(() => null)).transform((v) => v || null),
});
// ───── Correo (los esquemas de `@xiltrion/correo`, con un nombre para reconocerlo)
export const emailSourceSchema = z.object({ kind: z.literal('EMAIL'), name, email: outboundEmailSchema });
export const certifiedEmailSourceSchema = z.object({ kind: z.literal('CERTIFIED_EMAIL'), name, certified: certifiedEmailSchema });
export const sourceInputSchema = z.discriminatedUnion('kind', [storageSourceSchema, certificateSourceSchema, emailSourceSchema, certifiedEmailSourceSchema]);
/** Separa los secretos de lo que se puede mostrar. `undefined` = conservar el guardado. */
export function splitSourceSecret(input) {
    if (input.kind === 'STORAGE') {
        const { secretAccessKey, ...settings } = input;
        return { settings, secret: secretAccessKey === undefined ? undefined : { secretAccessKey: secretAccessKey || null } };
    }
    if (input.kind === 'EMAIL') {
        const { settings: email, secret } = splitSecret(input.email);
        return { settings: { kind: 'EMAIL', name: input.name, email }, secret: secret === undefined ? undefined : { secret: secret || null } };
    }
    if (input.kind === 'CERTIFIED_EMAIL') {
        const { clientSecret, ...certified } = input.certified;
        return { settings: { kind: 'CERTIFIED_EMAIL', name: input.name, certified }, secret: clientSecret === undefined ? undefined : { clientSecret: clientSecret || null } };
    }
    const { p12Base64, password, ...settings } = input;
    if (p12Base64 === undefined || p12Base64 === null || p12Base64 === '')
        return { settings, secret: password === undefined ? undefined : { password: password ?? '' } };
    return { settings, secret: { p12Base64, password: password ?? '' } };
}
/**
 * A dónde se le entrega el secreto. El guardado solo se reutiliza si esto no cambió. El certificado no tiene
 * destino: el .p12 y su contraseña van juntos.
 */
export function sourceTarget(s) {
    if (s.kind === 'STORAGE')
        return `s3:${(s.endpoint ?? 'aws').toLowerCase()}:${s.bucket}:${s.accessKeyId}`;
    if (s.kind === 'EMAIL')
        return outboundEmailTarget(s.email);
    if (s.kind === 'CERTIFIED_EMAIL')
        return certifiedEmailTarget(s.certified);
    return 'certificate';
}
/** Asignar (o quitar con `sourceId: null`) la fuente que una app usa para un uso. */
export const sourceAssignmentSchema = z.object({
    appCode: z.string().regex(/^[a-z][a-z0-9-]{1,30}$/),
    kind: z.enum(SOURCE_KINDS),
    slot: z.enum(ALL_SOURCE_SLOTS),
    sourceId: z.string().uuid().nullable(),
}).refine((a) => isSlotOf(a.kind, a.slot), { message: 'Ese uso no corresponde al tipo de fuente', path: ['slot'] });
/**
 * Probar una fuente sin guardarla (con el formulario) o la guardada (sin él). Con `sendTest`, el correo saliente
 * además envía un correo de prueba a quien prueba.
 */
export const testSourceSchema = z.object({ config: sourceInputSchema.optional(), sendTest: z.boolean().default(false) });
/**
 * Traspaso único de una fuente que la app ya tenía (`POST /api/v1/service/entities/:slug/sources/import`): la suite
 * la crea y se la asigna a la app que llama, solo si ese uso aún no tiene fuente. Si ya la tiene, no toca nada.
 */
export const sourceImportSchema = z.object({
    slot: z.enum(ALL_SOURCE_SLOTS),
    source: sourceInputSchema,
}).refine((i) => isSlotOf(i.source.kind, i.slot), { message: 'Ese uso no corresponde al tipo de fuente', path: ['slot'] });
//# sourceMappingURL=fuentes.js.map